import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
    Plus, Search, Users, Edit, Trash2, Check, Shield, UserCog, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
    { value: 'admin', label: 'Admin', icon: Shield, color: 'bg-red-100 text-red-700', description: 'Tüm yetkilere sahip' },
    { value: 'ust_yonetici', label: 'Üst Yönetici', icon: Shield, color: 'bg-purple-100 text-purple-700', description: 'Tüm programı GÖRÜR (Talepler hariç salt-okunur)' },
    { value: 'bar_sef', label: 'Bar Şef', icon: UserCog, color: 'bg-amber-100 text-amber-800', description: 'Tüm barlar + Talepler (düzenle/sil) + Depo/Sayım + Ürünler (görüntüleme)' },
    { value: 'yonetici', label: 'Yönetici', icon: UserCog, color: 'bg-indigo-100 text-indigo-700', description: 'Sadece atanan bölüm(ler)de tam işlem yetkisi' },
    { value: 'personel', label: 'Personel (Operasyon)', icon: User, color: 'bg-emerald-100 text-emerald-700', description: 'Atandığı departmanın iş kuyruğunu yönetir' },
    { value: 'manager', label: 'Yönetim (eski)', icon: UserCog, color: 'bg-blue-100 text-blue-700', description: 'Dashboard, Bar Stok ve Raporlar' },
    { value: 'staff', label: 'Personel (Bar)', icon: User, color: 'bg-green-100 text-green-700', description: 'Sadece yetkili barlar' },
    { value: 'garson', label: 'Garson', icon: User, color: 'bg-orange-100 text-orange-700', description: 'Sadece bar sayımı (ad-soyad girerek)' },
];

const SECTION_OPTIONS = [
    { value: 'on_buro', label: 'Operasyon · Ön Büro' },
    { value: 'teknik', label: 'Operasyon · Teknik' },
    { value: 'kat_hizmetleri', label: 'Operasyon · Kat Hizmetleri' },
    { value: 'servis', label: 'Operasyon · Servis' },
];

const DEPARTMENT_OPTIONS = [
    { value: 'on_buro', label: 'Ön Büro' },
    { value: 'teknik', label: 'Teknik' },
    { value: 'kat_hizmetleri', label: 'Kat Hizmetleri' },
    { value: 'servis', label: 'Servis' },
];

const BAR_OPTIONS = [
    { value: 'lobby', label: 'Lobby Bar' },
    { value: 'servis', label: 'Servis Bar' },
    { value: 'vitamin', label: 'Vitamin Bar' },
    { value: 'havuz', label: 'Havuz Bar' },
    { value: 'plaj', label: 'Plaj Bar' },
    { value: 'amfi', label: 'Amfi Bar' },
    { value: 'teras', label: 'Teras Bar' },
    { value: 'alacarte', label: 'Alacarte Bar' },
    { value: 'minibar', label: 'Minibar' },
];

export function UserManagement() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'staff',
        allowed_bars: [],
        allowed_sections: [],
        department: '',
    });

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/users`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUsers(data);
            } else if (res.status === 403) {
                toast.error('Bu sayfaya erişim yetkiniz yok');
            }
        } catch (error) {
            console.error('Load error:', error);
            toast.error('Kullanıcılar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(u => 
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    const openCreateDialog = () => {
        setEditingUser(null);
        setFormData({
            name: '', email: '', password: '', role: 'staff',
            allowed_bars: [], allowed_sections: [], department: '',
        });
        setDialogOpen(true);
    };

    const openEditDialog = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name || '',
            email: user.email || '',
            password: '',
            role: user.role || 'staff',
            allowed_bars: user.allowed_bars || [],
            allowed_sections: user.allowed_sections || [],
            department: user.department || '',
        });
        setDialogOpen(true);
    };

    const toggleBar = (barId) => {
        const bars = formData.allowed_bars || [];
        if (bars.includes(barId)) {
            setFormData({...formData, allowed_bars: bars.filter(b => b !== barId)});
        } else {
            setFormData({...formData, allowed_bars: [...bars, barId]});
        }
    };

    const toggleSection = (s) => {
        const list = formData.allowed_sections || [];
        if (list.includes(s)) setFormData({ ...formData, allowed_sections: list.filter(x => x !== s) });
        else setFormData({ ...formData, allowed_sections: [...list, s] });
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.email) {
            toast.error('Ad ve email zorunludur');
            return;
        }

        if (!editingUser && !formData.password) {
            toast.error('Şifre zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingUser 
                ? `${API_URL}/api/users/${editingUser.id}`
                : `${API_URL}/api/users`;
            
            const payload = { ...formData };
            if (!payload.password) delete payload.password;

            const res = await fetch(url, {
                method: editingUser ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                toast.success(editingUser ? 'Kullanıcı güncellendi' : 'Kullanıcı oluşturuldu');
                setDialogOpen(false);
                setEditingUser(null);
                loadUsers();
            } else {
                const error = await res.json();
                toast.error(error.detail || 'İşlem başarısız');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleDelete = async (user) => {
        if (user.id === currentUser?.id) {
            toast.error('Kendi hesabınızı silemezsiniz');
            return;
        }
        if (!window.confirm(`"${user.name}" kullanıcısını silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/users/${user.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                toast.success(`"${user.name}" silindi`);
                setUsers(prev => prev.filter(u => u.id !== user.id));
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Silme işlemi başarısız');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const getRoleBadge = (role) => {
        const roleConfig = ROLE_OPTIONS.find(r => r.value === role);
        if (!roleConfig) return <Badge variant="outline">{role}</Badge>;
        return (
            <Badge className={roleConfig.color}>
                {roleConfig.label}
            </Badge>
        );
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid gap-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="user-management-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Kullanıcı Yönetimi</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Toplam {users.length} kullanıcı
                    </p>
                </div>
                <Button 
                    onClick={openCreateDialog}
                    className="bg-orwion-green hover:bg-orwion-green-hover" 
                    data-testid="create-user-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Yeni Kullanıcı
                </Button>
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Ad veya email ile ara..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Users List */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                        <tr className="bg-slate-50 border-b">
                            <th className="text-left p-4 font-medium text-slate-600">Kullanıcı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Email</th>
                            <th className="text-left p-4 font-medium text-slate-600">Rol</th>
                            <th className="text-left p-4 font-medium text-slate-600">Yetkili Barlar</th>
                            <th className="text-left p-4 font-medium text-slate-600">Durum</th>
                            <th className="text-right p-4 font-medium text-slate-600">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="p-8 text-center text-slate-500">
                                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Henüz kullanıcı yok</p>
                                </td>
                            </tr>
                        ) : (
                            filteredUsers.map((user) => (
                                <tr key={user.id} className="border-b hover:bg-slate-50">
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                                <User className="w-4 h-4 text-slate-500" />
                                            </div>
                                            <span className="font-medium">{user.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-slate-600">{user.email}</td>
                                    <td className="p-4">{getRoleBadge(user.role)}</td>
                                    <td className="p-4">
                                        {user.role === 'staff' && user.allowed_bars?.length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {user.allowed_bars.map(barId => {
                                                    const bar = BAR_OPTIONS.find(b => b.value === barId);
                                                    return bar ? (
                                                        <Badge key={barId} variant="outline" className="text-xs">
                                                            {bar.label}
                                                        </Badge>
                                                    ) : null;
                                                })}
                                            </div>
                                        ) : (
                                            <span className="text-slate-400 text-sm">
                                                {user.role === 'admin' ? 'Tümü' : user.role === 'manager' ? 'Tümü' : '-'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-4">
                                        <Badge className={user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                            {user.is_active !== false ? 'Aktif' : 'Pasif'}
                                        </Badge>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                                onClick={() => openEditDialog(user)}
                                                data-testid={`edit-user-${user.id}`}
                                            >
                                                <Edit className="w-3.5 h-3.5 mr-1.5" />
                                                Düzenle
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                className="text-red-500 border-red-200 hover:bg-red-50"
                                                onClick={() => handleDelete(user)}
                                                disabled={user.id === currentUser?.id}
                                                data-testid={`delete-user-${user.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                                Sil
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Ad Soyad *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Örn: Ahmet Yılmaz"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                placeholder="ornek@email.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{editingUser ? 'Yeni Şifre (boş bırakılabilir)' : 'Şifre *'}</Label>
                            <Input
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({...formData, password: e.target.value})}
                                placeholder={editingUser ? 'Değiştirmek için yazın...' : '••••••'}
                                autoComplete="new-password"
                                data-testid="user-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Rol *</Label>
                            <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                                <SelectTrigger data-testid="um-role-trigger">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLE_OPTIONS.map(role => (
                                        <SelectItem key={role.value} value={role.value} textValue={role.label}>
                                            <div className="flex items-start gap-2 py-0.5">
                                                <role.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{role.label}</span>
                                                    <span className="text-[11px] text-slate-500 leading-tight">{role.description}</span>
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {(() => {
                                const desc = ROLE_OPTIONS.find(r => r.value === formData.role)?.description;
                                return desc ? <p className="text-[11px] text-slate-500 pl-1 leading-relaxed" data-testid="um-role-description">{desc}</p> : null;
                            })()}
                        </div>

                        {formData.role === 'staff' && (
                            <div className="space-y-2">
                                <Label>Yetkili Barlar</Label>
                                <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg">
                                    {BAR_OPTIONS.map(bar => (
                                        <div key={bar.value} className="flex items-center gap-2">
                                            <Checkbox
                                                id={bar.value}
                                                checked={formData.allowed_bars?.includes(bar.value)}
                                                onCheckedChange={() => toggleBar(bar.value)}
                                            />
                                            <label htmlFor={bar.value} className="text-sm cursor-pointer">
                                                {bar.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {formData.role === 'yonetici' && (
                            <div className="space-y-2" data-testid="um-sections-block">
                                <Label>Yetkili Bölümler (allowed_sections)</Label>
                                <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg">
                                    {SECTION_OPTIONS.map(s => (
                                        <div key={s.value} className="flex items-center gap-2">
                                            <Checkbox
                                                id={`sec-${s.value}`}
                                                checked={formData.allowed_sections?.includes(s.value)}
                                                onCheckedChange={() => toggleSection(s.value)}
                                                data-testid={`um-section-${s.value}`}
                                            />
                                            <label htmlFor={`sec-${s.value}`} className="text-sm cursor-pointer">{s.label}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {formData.role === 'personel' && (
                            <div className="space-y-2" data-testid="um-department-block">
                                <Label>Departman</Label>
                                <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                                    <SelectTrigger data-testid="um-department-select"><SelectValue placeholder="Departman seçin" /></SelectTrigger>
                                    <SelectContent>
                                        {DEPARTMENT_OPTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleSubmit}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {editingUser ? 'Güncelle' : 'Oluştur'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default UserManagement;
